import React, { useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import {
  ColorValues,
  Colors,
  Flex,
  IconShare,
  IconWonFilled,
} from '@banhala/ui-kit';
import { scheduleOnRN } from 'react-native-worklets';
import ReAnimated, {
  useAnimatedProps,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useScrollThresholdValue } from '../../hooks/useScrollThresholdValue';
import { useForegroundInterval } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedState,
} from 'shared/src/hooks/retainedState';
import { HTMLText } from 'shared/src/component/HTMLText';
import { isAndroid } from 'shared/src/utils';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
} from '../../constant/scrollValue';

const ICON_SIZE = 18;
const ANIMATION_DURATION = 200;
const TOGGLE_INTERVAL = 1200;

const affiliateFloatingViewLoggedToken = retainedState(false);

interface AffiliateFloatingButtonProps {
  text: string;
  marginBottom: number;
  onPress: () => void;
  onView: () => void;
}

export const AffiliateFloatingButton = ({
  text,
  marginBottom,
  onPress,
  onView,
}: AffiliateFloatingButtonProps) => {
  const visibilityValue = useScrollThresholdValue(
    SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
    SCROLL_ANIMATION_DURATION,
  );

  const rewardOpacity = useSharedValue(0);
  const shareOpacity = useSharedValue(1);
  const showReward = useRef(true);
  const [hasViewed, setHasViewed] = useRetainedState(
    affiliateFloatingViewLoggedToken,
  );

  const handleView = useCallback(() => {
    // 방문당 1회 — remount(같은 visit) 재발화로 VIEW 중복 방지.
    if (hasViewed) return;
    onView();
    setHasViewed(true);
  }, [onView, hasViewed, setHasViewed]);

  const animatedStyle = useAnimatedStyle(() => ({
    position: 'absolute',
    bottom: marginBottom + 16 + 32,
    left: '50%',
    transform: [{ translateX: '-50%' }],
    opacity: isAndroid
      ? visibilityValue.value > 0.5 ? 1 : 0
      : visibilityValue.value,
  }));

  const animatedProps = useAnimatedProps(() => ({
    pointerEvents:
      visibilityValue.value > 0.5
        ? ('auto' as const)
        : ('none' as const),
  }));

  const shareAnimatedStyle = useAnimatedStyle(() => ({
    opacity: shareOpacity.value,
  }));

  const rewardAnimatedStyle = useAnimatedStyle(() => ({
    opacity: rewardOpacity.value,
  }));

  useAnimatedReaction(
    () => visibilityValue.value > 0.5,
    (isVisible, prevIsVisible) => {
      if (isVisible && !prevIsVisible) {
        scheduleOnRN(handleView);
      }
    },
  );

  useForegroundInterval(() => {
    showReward.current = !showReward.current;
    const nextShowReward = showReward.current;

    rewardOpacity.value = withTiming(nextShowReward ? 1 : 0, {
      duration: ANIMATION_DURATION,
    });
    shareOpacity.value = withTiming(nextShowReward ? 0 : 1, {
      duration: ANIMATION_DURATION,
    });
  }, TOGGLE_INTERVAL, { immediate: false });

  return (
    <ReAnimated.View style={animatedStyle} animatedProps={animatedProps}>
      <Flex.Row
        onPress={onPress}
        alignItems={'center'}
        gap={4}
        padding={{ horizontal: 14, vertical: 10 }}
        backgroundColorValue={ColorValues.white}
        borderRadius={50}
        shadowStep={'large'}
      >
        <Flex width={ICON_SIZE} height={ICON_SIZE}>
          <ReAnimated.View
            style={[StyleSheet.absoluteFill, styles.iconCenter, shareAnimatedStyle]}
          >
            <IconShare size={ICON_SIZE} fill={Colors.content_secondary} />
          </ReAnimated.View>
          <ReAnimated.View
            style={[StyleSheet.absoluteFill, styles.iconCenter, rewardAnimatedStyle]}
          >
            <IconWonFilled size={ICON_SIZE} fill={ColorValues.orange30} />
          </ReAnimated.View>
        </Flex>
        <HTMLText type="subtitle3" color="content_secondary">
          {text}
        </HTMLText>
      </Flex.Row>
    </ReAnimated.View>
  );
};

const styles = StyleSheet.create({
  iconCenter: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
